import { useEffect, useState, useMemo, useRef, useCallback } from 'react';
import axios from 'axios';
import styled from 'styled-components';
import { useLocation } from 'react-router-dom';
import { parseKeywords, formatKeywords } from '../../utils/keywordUtils';
import { normalizeTag } from '../../utils/tagUtils';
import { CloseIcon } from '../TipSheetIcons/close-icon';
import { ArchiveFilterChoices } from './ArchiveFilterChoices.jsx';
import { ArchiveKeywordSearch } from './ArchiveKeywordSearch.jsx';
import { ArchiveTopicsFilter } from './ArchiveTopicsFilter.jsx';
import { ArchiveBillFilter } from './ArchiveBillFilter.jsx';
import { ArchiveLawmakerSelector } from './ArchiveLawmakerSelector.jsx';
import { RenderCards } from '../Stories/Stories.jsx';
import { ArchiveOffCanvasFilter } from './ArchiveOffCanvasFilter.jsx';

const ActiveFilterBar = styled.div`
  position: sticky;
  top: 0;
  z-index: 1;

  @media (min-width: 769px) {
    display: none;
  }
`;

const ArchiveSearchLayout = styled.div`
  display: flex;
  gap: 1rem;
  align-items: flex-start;
  width: 100%;
  max-width: 2200px;
  margin: 0 auto;

  @media (max-width: 768px) {
    display: block;
    max-width: 100%;
    margin: 0;
  }
`;

const ArchiveSidebar = styled.aside`
  display: none;

  @media (min-width: 769px) {
    display: block;
    width: 320px;
    flex-shrink: 0;
    background: #f4f7f9;
    border: 1px solid #ccc;
    padding: 1rem;
    position: sticky;
    top: 1rem;
    height: 100%;
    min-height: 100vh;
    overflow-y: auto;
    box-sizing: border-box;
  }
`;

const ArchiveMainContent = styled.div`
  width: 100%;

  @media (min-width: 769px) {
    width: calc(100% - 320px - 1rem);
  }
`;

const ArchiveSelectBar = styled.div`
  position: relative;
  display: flex;
  align-items: center;
  flex-direction: column;
  gap: 0.75rem;
  width: 100%;
`;

const YearDropdownWrapper = styled.div`
  position: relative;
  flex-direction: column;
  justify-content: center;
  width: 100%;
  ${(props) =>
    props.$isActive
      ? 'padding-block: 1rem; border-bottom: 1px solid #cccccc;'
      : ''}
`;

const YearDropdownLabel = styled.label`
  display: ${(props) => (props.$isActive ? 'block' : 'none')};
  font-size: 0.75rem;
  font-weight: 600;
  color: #666666;
  text-transform: uppercase;
  margin-bottom: 0.25rem;
`;

const YearDropdownItem = styled.label`
  position: relative;
  display: flex;
  align-items: center;
  padding: 0.5rem 0.5rem;
  cursor: pointer;

  input[type='radio'] {
    opacity: 0;
    width: 0;
    height: 0;
    position: absolute;
  }

  .custom-checkbox {
    width: 20px;
    height: 20px;
    border: 2px solid #cccccc;
    background: #fafafa;
    display: inline-block;
    position: relative;
    margin-right: 0.5rem;
    transition: background 0.2s;
    box-shadow: 0px 2px 3px 0px #0000000d inset;
  }

  input[type='radio']:checked + .custom-checkbox {
    background: #0a819f;
    border-color: #0a819f;
  }

  .custom-checkbox:after {
    content: '';
    position: absolute;
    display: none;
  }

  input[type='radio']:checked + .custom-checkbox:after {
    display: block;
  }

  .custom-checkbox:after {
    left: 5px;
    top: -1px;
    width: 6px;
    height: 12px;
    border: solid #fff;
    border-width: 0 3px 3px 0;
    transform: rotate(45deg);
    content: '';
    position: absolute;
  }
`;


const ArchiveHeaderWrapper = styled.div`
  background: #fcd70b;
  box-shadow: 3px 4px 4px 0px #0000001a;
  width: 100%;
  padding: 1rem 4rem;

  @media (max-width: 1440px) {
    padding: 1rem 2rem;
  }

  @media (max-width: 768px) {
    padding: 1rem;
  }
`;

const ArchiveHeader = styled.h2`
  font-size: 1rem;
  font-weight: 400;
  color: #666666;
  line-height: 1.5;
  margin: 0 auto;
  width: 100%;
  max-width: 2200px;
`;

const ArchiveContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 100%;
  max-width: 2200px;
  margin: 0 auto;
`;

const RenderCardsWrapper = styled.div`
  padding: 2rem 4rem;
  isolation: isolate;

  @media (max-width: 1440px) {
    padding: 2rem;
  }

  @media (max-width: 768px) {
    padding: 1rem;
  }
`;

const FilterWrap = styled.div`
  background-color: #f3f9fa;
  position: relative;
  display: flex;
  justify-content: center;
  align-items: center;
  height: calc(100vh - 5.375rem);

  @media (max-width: 1440px) {
    padding: 0 2rem;
  }

  @media (max-width: 768px) {
    padding: 0 1rem;
  }
`;

const FilterContainer = styled.div`
  padding: 4rem;
  background-color: #ffffff;
  box-shadow: 3px 4px 4px 0px #0000001a;
  border: 1px solid #cccccc;
  width: 100%;
  max-width: 56.25rem;
  gap: 2.5rem;
  display: flex;
  flex-direction: column;
  margin: 0 auto;

  @media (max-width: 600px) {
    padding: 2rem;
    gap: 2rem;
  }
`;

const FilterTitle = styled.h2`
  font-size: 1.5rem;
  color: #0e4c60;
  margin: 0;
`;

const SessionYearWrapper = styled.div`
  position: relative;
  display: flex;

  @media (max-width: 600px) {
    width: 100%;
  }
`;

const SessionYearBoxes = styled.div`
  display: flex;
  gap: 0.5rem;
  align-items: center;

  @media (max-width: 768px) {
    flex-direction: column;
    width: 100%;
    align-items: flex-start;
  }
`;

const SessionYearTitle = styled.h3`
  font-size: 1.25rem;
  color: #666666;
  margin: 0;
`;

const SessionYearCheckbox = styled.label`
  position: relative;
  display: inline-flex;
  align-items: center;
  cursor: pointer;
  margin-right: 1rem;
  font-weight: bold;

  input[type='radio'] {
    opacity: 0;
    width: 0;
    height: 0;
    position: absolute;
  }

  .custom-checkbox {
    width: 20px;
    height: 20px;
    border: 2px solid #cccccc;
    background: #fafafa;
    display: inline-block;
    position: relative;
    margin-right: 0.5rem;
    transition: background 0.2s;
    box-shadow: 0px 2px 3px 0px #0000000d inset;
  }

  input[type='radio']:checked + .custom-checkbox {
    background: #0a819f;
    border-color: #0a819f;
  }

  .custom-checkbox:after {
    content: '';
    position: absolute;
    display: none;
  }

  input[type='radio']:checked + .custom-checkbox:after {
    display: block;
  }

  .custom-checkbox:after {
    left: 5px;
    top: -1px;
    width: 6px;
    height: 12px;
    border: solid #fff;
    border-width: 0 3px 3px 0;
    transform: rotate(45deg);
    content: '';
    position: absolute;
  }
`;

const PillText = styled.span`
  font-size: 0.875rem;
  color: #212121;
  white-space: nowrap;
  font-style: ${(props) => (props.$isUnquoted ? 'italic' : 'normal')};
`;

const PillCloseButton = styled.button`
  background: transparent;
  border: none;
  cursor: pointer;
  margin-left: 0.5rem;
  padding: 0.125rem;
  display: flex;
  align-items: center;
  color: #666666;

  &:hover {
    color: #000000;
  }

  &:focus {
    outline: 2px solid #0e4c60;
    outline-offset: 2px;
    border-radius: 50%;
  }
`;

const ArchiveFilterContainer = styled.div`
  display: flex;
  gap: 0.5rem;

  @media (max-width: 768px) {
    flex-direction: column;
    width: 100%;
  }
`;

const ArchiveHardReturn = styled.hr`
  border: 1px solid #cccccc;
  margin: ${(props) => (props.$noBottom ? '1rem 0 0' : '1rem 0')};
`;

const SearchButton = styled.button`
	background-color: #0a819f;
	color: #ffffff;
	border: none;
	padding: 0.5rem 1rem;
	border-radius: 0.25rem;
	cursor: pointer;
	width: 100%;

	&:disabled {
		background-color: #cccccc;
		color: #000;
		cursor: not-allowed;
		opacity: 0.7;
	}
`;

const FilterChoicesInternal = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
  width: 100%;
  margin: -1rem auto;
  align-items: center;
`;

const FilterPill = styled.div`
  display: flex;
  align-items: center;
  border-radius: 0.375rem;
  padding: 0.25rem 0.5rem;
  box-shadow: 0px 1px 0px 0px #cccccc;
  border: 1px solid #cccccc;
  background: #fafafa;

  & svg {
    width: 0.75rem;
    height: 0.75rem;
  }
`;

const FilterChoicesLabel = styled.div`
  text-transform: uppercase;
  font-weight: 700;
  color: #666666;
  width: 100%;
  font-size: 0.875rem;
  margin-bottom: 0.5rem;
`;

const ClearAllButton = styled.button`
  background: transparent;
  border: none;
  color: #0a819f;
  cursor: pointer;
  font-weight: 700;
  padding: 0.25rem;
  text-decoration: underline;
`;

const LoadMoreButton = styled.button`
  background-color: #0a819f;
  color: #fff;
  border: none;
  padding: 0.75rem 4rem;
  border-radius: 0.25rem;
  cursor: pointer;
  margin: 2rem auto 0 auto;
  display: block;
  font-size: 1rem;
  box-shadow: 0px 1px 0px 0px #cccccc;
`;

const ArchiveOffCanvasTrigger = styled.button`
  background: #0a819f;
  color: #fff;
  border: none;
  border-radius: 0.25rem;
  padding: 0.5rem 1rem;
  width: 100%;
  justify-content: center;
  cursor: pointer;
  align-items: center;
  display: flex;
  gap: 0.5rem;
`;

const ArchiveOffCanvasTriggerWrapper = styled.div`
  display: none;

  @media (max-width: 768px) {
    display: flex;
    position: fixed;
    right: 1rem;
    bottom: 1rem;
    z-index: 999;
    border-radius: 999px;

    &::before {
      content: '';
      position: absolute;
      top: -0.75rem;
      left: -0.75rem;
      right: -0.75rem;
      bottom: -0.75rem;
      background: rgba(255, 255, 255, 0.8);
      backdrop-filter: blur(2px);
      border-radius: 0.5rem;
      z-index: -1;
    }
  }

  &[data-hidden='true'] {
    display: none;
  }
`;

const SpinnerOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(255, 255, 255, 0.7);
  z-index: 9999;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const Spinner = styled.div`
  border: 6px solid #f3f3f3;
  border-top: 6px solid #0a819f;
  border-radius: 50%;
  width: 48px;
  height: 48px;
  animation: spin 1s linear infinite;
  @keyframes spin {
    0% {
      transform: rotate(0deg);
    }
    100% {
      transform: rotate(360deg);
    }
  }
`;

const ArchiveSearch = ({
  sessionYear,
  setSessionYear,
  archiveStats,
  keywords,
  setKeywords,
  performSearch,
  isMobile,
  topics,
  bills,
  legislators,
  selectedTopics,
  setSelectedTopics,
  selectedBills,
  setSelectedBills,
  selectedLegislators,
  setSelectedLegislators,
  keywordInput,
  setKeywordInput,
}) => {
  const searchInputRef = useRef(null);

  const uniqueYears = useMemo(() => {
    if (!archiveStats?.by_year) return [];
    const years = Object.keys(archiveStats.by_year)
      .filter((year) => parseInt(year) >= 2022)
      .sort((a, b) => b - a)
      .map((year) => ({ value: year, label: year }));
    return years;
  }, [archiveStats]);

  useEffect(() => {
    if (uniqueYears?.length > 0 && sessionYear?.length === 0) {
      setSessionYear([uniqueYears[0].value]);
    }
  }, [uniqueYears, sessionYear.length, setSessionYear]);

  const handleRemoveKeyword = (keywordToRemove) => {
    const keywordsArray = parseKeywords(keywords);
    const updatedArray = keywordsArray.filter((keyword) => keyword !== keywordToRemove);
    const updatedKeywords = formatKeywords(updatedArray);

    setKeywords(updatedKeywords);
    performSearch(
      updatedKeywords,
      sessionYear,
      selectedBills,
      selectedLegislators,
      selectedTopics
    );
  };

  const handleRemoveLegislator = (legislatorToRemove) => {
    const updatedLegislators = selectedLegislators.filter(
      (legislator) => legislator !== legislatorToRemove
    );
    setSelectedLegislators(updatedLegislators);
    performSearch(
      keywords,
      sessionYear,
      selectedBills,
      updatedLegislators,
      selectedTopics
    );
  };

  const handleRemoveTopic = (topicToRemove) => {
    const updatedTopics = selectedTopics.filter((topic) => topic !== topicToRemove);
    setSelectedTopics(updatedTopics);
    performSearch(
      keywords,
      sessionYear,
      selectedBills,
      selectedLegislators,
      updatedTopics
    );
  };

  const handleRemoveBill = (billToRemove) => {
    const updatedBills = selectedBills.filter((bill) => bill !== billToRemove);
    setSelectedBills(updatedBills);
    performSearch(
      keywords,
      sessionYear,
      updatedBills,
      selectedLegislators,
      selectedTopics
    );
  };

  const handleClearAll = () => {
    setKeywords('');
    setSelectedLegislators([]);
    setSelectedTopics([]);
    setSelectedBills([]);
    performSearch('', sessionYear, [], [], []);
  };

  const keywordsArray = parseKeywords(keywords);
  const hasAnyFilters =
    keywordsArray.length > 0 ||
    selectedLegislators.length > 0 ||
    selectedTopics.length > 0 ||
    selectedBills.length > 0;

  return (
    <FilterWrap>
      <FilterContainer>
        <FilterTitle>Search Tip Sheet Archive</FilterTitle>
        <div>
          <>
            <SessionYearWrapper>
              <SessionYearBoxes>
                <SessionYearTitle>Years:</SessionYearTitle>
                {uniqueYears.map((year) => (
                  <SessionYearCheckbox key={year.value}>
                    <input
                      type="radio"
                      checked={sessionYear.includes(year.value)}
                      onChange={() => setSessionYear([year.value])}
                      id={`year-${year.value}`}
                      name="sessionYear"
                    />
                    <span className="custom-checkbox"></span>
                    {year.label}
                  </SessionYearCheckbox>
                ))}
              </SessionYearBoxes>
            </SessionYearWrapper>
            <ArchiveHardReturn />
          </>
          <ArchiveFilterContainer>
            <ArchiveKeywordSearch
              keywords={keywords}
              setKeywords={setKeywords}
              isMobile={true}
              inputRef={searchInputRef}
              onInputChange={setKeywordInput}
              isActive={false}
            />
            <ArchiveTopicsFilter
              topics={topics}
              selectedTopics={selectedTopics}
              setSelectedTopics={setSelectedTopics}
              isMobile={true}
              isActive={false}
            />
            <ArchiveBillFilter
              bills={bills}
              selectedBills={selectedBills}
              setSelectedBills={setSelectedBills}
              isMobile={true}
              isActive={false}
            />
            <ArchiveLawmakerSelector
              legislators={legislators}
              selectedLegislators={selectedLegislators}
              setSelectedLegislators={setSelectedLegislators}
              isMobile={true}
              isActive={false}
            />
          </ArchiveFilterContainer>
          <ArchiveHardReturn $noBottom={true} />
        </div>
        {hasAnyFilters ? (
          <FilterChoicesInternal>
            <FilterChoicesLabel>Search Parameters:</FilterChoicesLabel>
            {keywordsArray.map((keyword, index) => {
              const isQuoted = /^".*"$/.test(keyword);
              return (
                <FilterPill key={`keyword-${keyword}-${index}`}>
                  <PillText $isUnquoted={!isQuoted}>{keyword}</PillText>
                  <PillCloseButton
                    onClick={() => handleRemoveKeyword(keyword)}
                    aria-label={`Remove ${keyword} from search`}
                  >
                    <CloseIcon />
                  </PillCloseButton>
                </FilterPill>
              );
            })}
            {selectedLegislators.map((legislator, index) => {
              const legislatorObj = legislators.find(
                (l) => l.value === legislator
              );
              return (
                <FilterPill key={`legislator-${legislator}-${index}`}>
                  <PillText>
                    {legislatorObj ? legislatorObj.label : legislator}
                  </PillText>
                  <PillCloseButton
                    onClick={() => handleRemoveLegislator(legislator)}
                    aria-label={`Remove ${
                      legislatorObj ? legislatorObj.label : legislator
                    } from search`}
                  >
                    <CloseIcon />
                  </PillCloseButton>
                </FilterPill>
              );
            })}
            {selectedTopics.map((topic, index) => {
              const topicObj = topics.find((t) => t.value === topic);
              return (
                <FilterPill key={`topic-${topic}-${index}`}>
                  <PillText>{topicObj ? topicObj.label : topic}</PillText>
                  <PillCloseButton
                    onClick={() => handleRemoveTopic(topic)}
                    aria-label={`Remove ${
                      topicObj ? topicObj.label : topic
                    } from search`}
                  >
                    <CloseIcon />
                  </PillCloseButton>
                </FilterPill>
              );
            })}
            {selectedBills.map((bill, index) => {
              const billObj = bills.find((b) => b.value === bill);
              return (
                <FilterPill key={`bill-${bill}-${index}`}>
                  <PillText>{billObj ? billObj.label : bill}</PillText>
                  <PillCloseButton
                    onClick={() => handleRemoveBill(bill)}
                    aria-label={`Remove ${
                      billObj ? billObj.label : bill
                    } from search`}
                  >
                    <CloseIcon />
                  </PillCloseButton>
                </FilterPill>
              );
            })}
            <ClearAllButton onClick={handleClearAll}>Clear All</ClearAllButton>
          </FilterChoicesInternal>
        ) : null}
        <form
          onSubmit={(e) => {
            e.preventDefault();
            performSearch(
              keywords,
              sessionYear,
              selectedBills,
              selectedLegislators,
              selectedTopics
            );
          }}
          style={{ position: 'relative' }}
        >
          <SearchButton type="submit" disabled={!!keywordInput}>
            Search Archive
          </SearchButton>
        </form>
      </FilterContainer>
    </FilterWrap>
  );
};

export default function Archive() {
  const location = useLocation();
  const [keywordInput, setKeywordInput] = useState('');
  const [searchStories, setSearchStories] = useState([]);
  const [searchActive, setSearchActive] = useState(false);
  const [sessionYear, setSessionYear] = useState([]);
  const [archiveStats, setArchiveStats] = useState(null);
  const [keywords, setKeywords] = useState('');
  const [legislators, setLegislators] = useState([]);
  const [bills, setBills] = useState([]);
  const [topics, setTopics] = useState([]);
  const [selectedBills, setSelectedBills] = useState([]);
  const [selectedLegislators, setSelectedLegislators] = useState([]);
  const [selectedTopics, setSelectedTopics] = useState([]);
  const [visibleCount, setVisibleCount] = useState(100);
  const yearDropdownRef = useRef(null);
  const [offcanvasOpen, setOffcanvasOpen] = useState(false);
  const [yearDataCache, setYearDataCache] = useState(new Map());
  const [loading, setLoading] = useState(false);

  const resetArchive = () => {
    setSearchStories([]);
    setSearchActive(false);
    setSessionYear([]);
    setKeywords('');
    setLegislators([]);
    setBills([]);
    setTopics([]);
    setSelectedBills([]);
    setSelectedLegislators([]);
    setSelectedTopics([]);
    setVisibleCount(100);
    setOffcanvasOpen(false);
    setYearDataCache(new Map());
    setLoading(false);
  };

  useEffect(() => {
    if (location.pathname === '/archive') {
      resetArchive();
    }
  }, [location.pathname, location.state?.reset]);

  // Unique years for dropdown
  const uniqueYears = useMemo(() => {
    if (!archiveStats?.by_year) return [];
    return Object.keys(archiveStats.by_year)
      .filter((year) => parseInt(year) >= 2022)
      .sort((a, b) => b - a)
      .map((year) => ({ value: year, label: year }));
  }, [archiveStats]);

  // Reset visibleCount when new search is performed
  useEffect(() => {
    setVisibleCount(100);
  }, [searchStories]);

  useEffect(() => {
    const fetchArchiveStats = async () => {
      try {
        const axiosT = axios.create({
          baseURL: `${process.env.REACT_APP_FEATURED_API}`,
          headers: {
            Authorization: `JWT ${localStorage.getItem('token')}`,
          },
        });

        const response = await axiosT.get('stats/');
        if (response.data && response.data.archive) {
          setArchiveStats(response.data.archive);
        }
      } catch (err) {
        console.log('Error fetching archive stats:', err);
      }
    };

    fetchArchiveStats();
  }, []);

  useEffect(() => {
    const fetchBills = async () => {
      try {
        const axiosT = axios.create({
          baseURL: `${process.env.REACT_APP_FEATURED_API}`,
          headers: {
            Authorization: `JWT ${localStorage.getItem('token')}`,
          },
        });

        const response = await axiosT.get(
          `archive/?y=${sessionYear[0]}&q=bills`
        );
        if (response.data && response.data.bills) {
          setBills(response.data.bills);
        }
      } catch (err) {
        console.log('Error fetching bills:', err);
      }
    };

    const fetchTopics = async () => {
      try {
        const axiosT = axios.create({
          baseURL: `${process.env.REACT_APP_FEATURED_API}`,
          headers: {
            Authorization: `JWT ${localStorage.getItem('token')}`,
          },
        });

        const response = await axiosT.get(
          `archive/?y=${sessionYear[0]}&q=topics`
        );
        if (response.data && response.data.topics) {
          setTopics(
            response.data.topics.filter((topic) => {
              const value =
                typeof topic === 'string'
                  ? topic
                  : topic?.value || topic?.label || null;
              return value && normalizeTag(value) !== 'unknown';
            })
          );
        }
      } catch (err) {
        console.log('Error fetching archive stats:', err);
      }
    };

    const fetchLegislators = async () => {
      try {
        const axiosT = axios.create({
          baseURL: `${process.env.REACT_APP_FEATURED_API}`,
          headers: {
            Authorization: `JWT ${localStorage.getItem('token')}`,
          },
        });

        const response = await axiosT.get(
          `archive/?y=${sessionYear[0]}&q=legislators`
        );
        if (response.data && response.data.legislators) {
          setLegislators(response.data.legislators);
        }
      } catch (err) {
        console.log('Error fetching archive stats:', err);
      }
    };

    fetchBills();
    fetchTopics();
    fetchLegislators();
  }, [sessionYear]);

  const performSearch = useCallback(async (
    searchTerm,
    sessionYear,
    selectedBills,
    selectedLegislators,
    selectedTopics
  ) => {
    setLoading(true);
    try {
      const axiosT = axios.create({
        baseURL: `${process.env.REACT_APP_FEATURED_API}`,
        headers: {
          Authorization: `JWT ${localStorage.getItem('token')}`,
        },
      });

      const currentYear = sessionYear[0];

      // Check cache first
      let yearData;
      if (yearDataCache.has(currentYear)) {
        yearData = yearDataCache.get(currentYear);
      } else {
        // Only fetch if not cached
        const yearResponse = await axiosT.get(`archive/?y=${currentYear}`);
        yearData = yearResponse.data.tipsheets || [];

        // Cache the result
        setYearDataCache((prev) => new Map(prev).set(currentYear, yearData));
      }

      // Helper: filter by selectedBills, selectedLegislators, selectedTopics
      const filterResults = (data) => {
        return data.filter((item) => {
          // Bill match: bid is a string, selectedBills is an array of strings
          const billMatch =
            selectedBills.length === 0 ||
            (item.bid && selectedBills.includes(item.bid));

          // Legislator match: selectedLegislators is array of pid (number or string)
          // personas is an array of objects with pid
          const legislatorMatch =
            selectedLegislators.length === 0 ||
            (item.tipsheet_tile_json &&
              Array.isArray(item.tipsheet_tile_json.personas) &&
              item.tipsheet_tile_json.personas.some((persona) =>
                selectedLegislators.includes(String(persona.pid))
              ));

          // Topic match: tags is an array of objects with tagname
          const topicMatch =
            selectedTopics.length === 0 ||
            (item.tipsheet_tile_json &&
              Array.isArray(item.tipsheet_tile_json.tags) &&
              item.tipsheet_tile_json.tags.some((tag) => {
                return selectedTopics.some((selected) => {
                  if (selected === 'public_safety') {
                    return tag.tagname === 'Public_Safety';
                  }
                  if (selected.startsWith('geo_')) {
                    return tag.tagname === selected;
                  }
                  // Capitalize first letter for all other topics
                  const normalizedSelected =
                    selected.charAt(0).toUpperCase() + selected.slice(1);
                  return tag.tagname === normalizedSelected;
                });
              }));

          return billMatch && legislatorMatch && topicMatch;
        });
      };

      if (!searchTerm?.trim()) {
        // No search term: just filter yearData
        setSearchStories(filterResults(yearData));
        setSearchActive(true);
        return;
      }

      // With search term: filter by relevance
      const response = await axiosT.get(
        `search/?p=archive&f=key&q=${encodeURIComponent(searchTerm)}`
      );
      const relevanceMap = response.data.tipsheets || {};

      const filteredResults = yearData
        .filter((item) => relevanceMap[item.id])
        .map((item) => ({
          ...item,
          relevance: relevanceMap[item.id].relevance,
        }))
        .sort((a, b) => b.relevance - a.relevance);

      setSearchStories(filterResults(filteredResults));
      setSearchActive(true);
    } catch (err) {
      console.log('Error fetching search data:', err);
    } finally {
      setLoading(false);
    }
  }, [yearDataCache]);

  useEffect(() => {
    if (!searchActive) return;
    performSearch(
      keywords,
      sessionYear,
      selectedBills,
      selectedLegislators,
      selectedTopics
    );
  }, [
    searchActive,
    keywords,
    sessionYear,
    selectedBills,
    selectedLegislators,
    selectedTopics,
    performSearch,
  ]);

  const selectedLegislatorsForFilterChoices = selectedLegislators.map((id) => {
    const match = legislators.find((l) => l.value === id);
    return match ? { value: id, label: match.label } : id;
  });

  const selectedBillsForFilterChoices = selectedBills.map((id) => {
    const match = bills.find((b) => b.value === id);
    return match ? match : id;
  });

  const renderArchiveSidebarContent = () => (
    <>
      <ArchiveFilterChoices
        tags={selectedTopics}
        setTags={setSelectedTopics}
        legislators={selectedLegislatorsForFilterChoices}
        setLegislators={setSelectedLegislators}
        selectedBills={selectedBillsForFilterChoices}
        setSelectedBills={setSelectedBills}
        keywords={keywords}
        setKeywords={setKeywords}
        setSearchActive={setSearchActive}
        setSearchStories={setSearchStories}
        searchInputRef={null}
        originalStories={[]}
        chamberFilter="both"
        setChamberFilter={() => {}}
        performSearch={performSearch}
        variant="sidebar"
      />
      <ArchiveSelectBar $isMobile={false}>
        {/* Year Selector */}
        <YearDropdownWrapper ref={yearDropdownRef} $isMobile={false} $isActive={searchActive}>
          <YearDropdownLabel $isActive={searchActive}>Year</YearDropdownLabel>
          {uniqueYears.map((year) => (
            <YearDropdownItem key={year.value}>
              <input
                type="radio"
                checked={sessionYear[0] === year.value}
                onChange={() => {
                  setSessionYear([year.value]);
                  performSearch(
                    keywords,
                    [year.value],
                    selectedBills,
                    selectedLegislators,
                    selectedTopics
                  );
                }}
                id={`active-year-${year.value}`}
                name="activeSessionYear"
              />
              <span className="custom-checkbox"></span>
              {year.label}
            </YearDropdownItem>
          ))}
        </YearDropdownWrapper>

        {/* Filters */}
        <ArchiveKeywordSearch
          keywords={keywords}
          setKeywords={(newKeywords) => {
            setKeywords(newKeywords);
            performSearch(
              newKeywords,
              sessionYear,
              selectedBills,
              selectedLegislators,
              selectedTopics
            );
          }}
          isMobile={false}
          isActive={true}
        />
        <ArchiveTopicsFilter
          topics={topics}
          selectedTopics={selectedTopics}
          setSelectedTopics={(newTopics) => {
            setSelectedTopics(newTopics);
            performSearch(
              keywords,
              sessionYear,
              selectedBills,
              selectedLegislators,
              newTopics
            );
          }}
          isMobile={false}
          isActive={true}
        />
        <ArchiveBillFilter
          bills={bills}
          selectedBills={selectedBills}
          setSelectedBills={(newBills) => {
            setSelectedBills(newBills);
            performSearch(
              keywords,
              sessionYear,
              newBills,
              selectedLegislators,
              selectedTopics
            );
          }}
          isMobile={false}
          isActive={true}
        />
        <ArchiveLawmakerSelector
          legislators={legislators}
          selectedLegislators={selectedLegislators}
          setSelectedLegislators={(newLegislators) => {
            setSelectedLegislators(newLegislators);
            performSearch(
              keywords,
              sessionYear,
              selectedBills,
              newLegislators,
              selectedTopics
            );
          }}
          isMobile={false}
          isActive={true}
        />
      </ArchiveSelectBar>
    </>
  );

  if (!searchActive) {
    return (
      <>
        {loading && (
          <SpinnerOverlay>
            <Spinner />
          </SpinnerOverlay>
        )}
        <ArchiveSearch
          sessionYear={sessionYear}
          setSessionYear={setSessionYear}
          archiveStats={archiveStats}
          keywords={keywords}
          setKeywords={setKeywords}
          performSearch={performSearch}
          isMobile={false}
          topics={topics}
          bills={bills}
          legislators={legislators}
          selectedTopics={selectedTopics}
          setSelectedTopics={setSelectedTopics}
          selectedBills={selectedBills}
          setSelectedBills={setSelectedBills}
          selectedLegislators={selectedLegislators}
          setSelectedLegislators={setSelectedLegislators}
          keywordInput={keywordInput}
          setKeywordInput={setKeywordInput}
        />
      </>
    );
  }

  // If searchActive is true, show filter bar and results
  return (
    <>
      <ActiveFilterBar>
        <ArchiveOffCanvasFilter
          isOpen={offcanvasOpen}
          setIsOpen={setOffcanvasOpen}
          sessionYear={sessionYear}
          setSessionYear={setSessionYear}
          archiveStats={archiveStats}
          keywords={keywords}
          setKeywords={setKeywords}
          performSearch={performSearch}
          topics={topics}
          bills={bills}
          legislators={legislators}
          selectedTopics={selectedTopics}
          setSelectedTopics={setSelectedTopics}
          selectedBills={selectedBills}
          setSelectedBills={setSelectedBills}
          selectedLegislators={selectedLegislators}
          setSelectedLegislators={setSelectedLegislators}
          uniqueYears={uniqueYears}
        />
        <ArchiveSelectBar $isMobile={false}>
          {/* Filters */}
          <ArchiveKeywordSearch
            keywords={keywords}
            setKeywords={(newKeywords) => {
              setKeywords(newKeywords);
              performSearch(
                newKeywords,
                sessionYear,
                selectedBills,
                selectedLegislators,
                selectedTopics
              );
            }}
            isMobile={false}
          />
          <ArchiveTopicsFilter
            topics={topics}
            selectedTopics={selectedTopics}
            setSelectedTopics={(newTopics) => {
              setSelectedTopics(newTopics);
              performSearch(
                keywords,
                sessionYear,
                selectedBills,
                selectedLegislators,
                newTopics
              );
            }}
            isMobile={false}
          />
          <ArchiveBillFilter
            bills={bills}
            selectedBills={selectedBills}
            setSelectedBills={(newBills) => {
              setSelectedBills(newBills);
              performSearch(
                keywords,
                sessionYear,
                newBills,
                selectedLegislators,
                selectedTopics
              );
            }}
            isMobile={false}
          />
          <ArchiveLawmakerSelector
            legislators={legislators}
            selectedLegislators={selectedLegislators}
            setSelectedLegislators={(newLegislators) => {
              setSelectedLegislators(newLegislators);
              performSearch(
                keywords,
                sessionYear,
                selectedBills,
                newLegislators,
                selectedTopics
              );
            }}
            isMobile={false}
          />
        </ArchiveSelectBar>
        <ArchiveFilterChoices
          tags={selectedTopics}
          setTags={(newTopics) => {
            setSelectedTopics(newTopics);
            performSearch(
              keywords,
              sessionYear,
              selectedBills,
              selectedLegislators,
              newTopics
            );
          }}
          legislators={selectedLegislatorsForFilterChoices}
          setLegislators={(newLegislators) => {
            setSelectedLegislators(newLegislators);
            performSearch(
              keywords,
              sessionYear,
              selectedBills,
              newLegislators,
              selectedTopics
            );
          }}
          selectedBills={selectedBillsForFilterChoices}
          setSelectedBills={setSelectedBills}
          keywords={keywords}
          setKeywords={setKeywords}
          setSearchActive={setSearchActive}
          setSearchStories={setSearchStories}
          searchInputRef={null}
          originalStories={[]}
          chamberFilter="both"
          setChamberFilter={() => {}}
          performSearch={performSearch}
          variant="mobile-dashboard"
        />
      </ActiveFilterBar>

      <ArchiveOffCanvasTriggerWrapper data-hidden={offcanvasOpen ? 'true' : 'false'}>
        <ArchiveOffCanvasTrigger onClick={() => setOffcanvasOpen(true)}>
          <svg
            width="17"
            height="17"
            viewBox="0 0 17 17"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
            aria-hidden="true"
            focusable="false"
          >
            <path
              d="M6.625 2.25H0.5M8.375 14.5H0.5M10.125 0.5V4M11.875 12.75V16.25M16.25 8.375H8.375M16.25 14.5H11.875M16.25 2.25H10.125M4.875 6.625V10.125M4.875 8.375H0.5"
              stroke="white"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
          Show Filters
        </ArchiveOffCanvasTrigger>
      </ArchiveOffCanvasTriggerWrapper>

      {loading && (
        <SpinnerOverlay>
          <Spinner />
        </SpinnerOverlay>
      )}
      {Array.isArray(searchStories) && searchStories.length > 0 ? (
        <>
          <ArchiveHeaderWrapper>
            <ArchiveHeader>
              ({searchStories.length} Archived Result
              {searchStories.length > 1 ? 's' : ''})
            </ArchiveHeader>
          </ArchiveHeaderWrapper>
          <ArchiveSearchLayout>
            <ArchiveSidebar>{renderArchiveSidebarContent()}</ArchiveSidebar>
            <ArchiveMainContent>
              <ArchiveContainer>
                <RenderCardsWrapper>
                  <RenderCards cards={searchStories.slice(0, visibleCount)} />
                  {visibleCount < searchStories.length && (
                    <LoadMoreButton
                      onClick={() => setVisibleCount(visibleCount + 100)}
                    >
                      Load More
                    </LoadMoreButton>
                  )}
                </RenderCardsWrapper>
              </ArchiveContainer>
            </ArchiveMainContent>
          </ArchiveSearchLayout>
        </>
      ) : (
        <>
          <ArchiveHeaderWrapper>
            <ArchiveHeader>No results found for your query.</ArchiveHeader>
          </ArchiveHeaderWrapper>
          <ArchiveSearchLayout>
            <ArchiveSidebar>{renderArchiveSidebarContent()}</ArchiveSidebar>
            <ArchiveMainContent>
              <ArchiveContainer>
                <RenderCardsWrapper>
                  <ArchiveHeader>Please update your search criteria.</ArchiveHeader>
                </RenderCardsWrapper>
              </ArchiveContainer>
            </ArchiveMainContent>
          </ArchiveSearchLayout>
        </>
      )}
    </>
  );
}
